import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, ShieldCheck } from "lucide-react";
import PoweredByTendStrat from "@/components/PoweredByTendStrat";
import GoogleButton, { OrDivider } from "@/components/auth/GoogleButton";
import MfaCodeForm from "@/components/auth/MfaCodeForm";
import ForgotPassword from "@/components/auth/ForgotPassword";
import VerifyEmailNotice from "@/components/auth/VerifyEmailNotice";

const BG = "https://images.unsplash.com/photo-1749799798138-df205862e744?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxvaWwlMjByaWclMjBjb25zdHJ1Y3Rpb24lMjBmYWNpbGl0eSUyMGFlcmlhbHxlbnwwfHx8fDE3Nzc5NzUxOTd8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, loginWithGoogle, completeMfa, resendVerification } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const nextPath = params.get("next") || "/dashboard";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("login");
  const [resolver, setResolver] = useState(null);
  const [unverified, setUnverified] = useState(false);
  const [resend, setResend] = useState({ sending: false, sent: false });

  const handle = (r) => {
    setLoading(false);
    if (r.ok) {
      const locked = r.user?.role !== "admin" && r.user?.plan_info?.effective_plan === "none";
      return nav(locked && nextPath === "/dashboard" ? "/plans" : nextPath);
    }
    if (r.mfaResolver) { setResolver(r.mfaResolver); setView("mfa"); setErr(""); return; }
    setUnverified(!!r.unverified);
    setErr(r.unverified ? "" : r.error);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    handle(await login(email, password));
  };

  const google = async () => {
    setErr(""); setLoading(true);
    handle(await loginWithGoogle());
  };

  const mfa = async (code) => {
    setErr(""); setLoading(true);
    handle(await completeMfa(resolver, code));
  };

  const doResend = async () => {
    setResend({ sending: true, sent: false });
    const r = await resendVerification(email, password);
    setResend({ sending: false, sent: r.ok });
    if (!r.ok) setErr(r.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left panel - branding */}
      <div
        className="hidden lg:block relative"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0,27,102,0.75), rgba(0,27,102,0.90)), url(${BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white grid place-items-center text-brand font-display font-bold text-xl rounded-sm">T</div>
            <div>
              <div className="font-display font-bold tracking-tight text-lg">TWIN</div>
              <div className="text-[11px] uppercase tracking-[0.2em] opacity-80">Tender Win Intelligence Navigator</div>
            </div>
          </div>

          <div>
            <h1 className="font-display text-5xl xl:text-6xl font-bold leading-[0.95] tracking-tight mb-6">
              Win more tenders.<br />
              <span className="text-warn">Decide with data.</span>
            </h1>
            <p className="text-white/80 text-[15px] max-w-md leading-relaxed">
              A five-stage decision framework for EPCs and industrial firms. TRCA · BNBD · PCEI · PCAB · TAPI.
              Built to compress your 120-day tender programme into a repeatable winning system.
            </p>

            <div className="mt-10 grid grid-cols-3 gap-6 pt-8 border-t border-white/15">
              <div>
                <div className="font-num text-3xl font-bold">120d</div>
                <div className="text-[11px] uppercase tracking-wider text-white/60 mt-1">to a winning system</div>
              </div>
              <div>
                <div className="font-num text-3xl font-bold">5</div>
                <div className="text-[11px] uppercase tracking-wider text-white/60 mt-1">framework modules</div>
              </div>
              <div>
                <div className="font-num text-3xl font-bold">1×</div>
                <div className="text-[11px] uppercase tracking-wider text-white/60 mt-1">central control room</div>
              </div>
            </div>
          </div>

          <div className="text-[11px] uppercase tracking-[0.2em] text-white/60">
            For Bid Managers · Project Leads · EPC Decision-makers
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12 bg-white">
        <div className="max-w-md w-full mx-auto">
          <div className="mb-10">
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted mb-3">Sign in</div>
            <h2 className="font-display text-4xl font-bold tracking-tight">
              {view === "forgot" ? "Reset password." : view === "mfa" ? "2-step verification." : "Welcome back."}
            </h2>
            <p className="text-ink-muted text-sm mt-2">Access your tender control room.</p>
          </div>

          {view === "forgot" && <ForgotPassword initialEmail={email} onBack={() => setView("login")} />}
          {view === "mfa" && (
            <MfaCodeForm onSubmit={mfa} onCancel={() => { setView("login"); setErr(""); }} error={err} loading={loading} />
          )}

          {view === "login" && (<>
          <GoogleButton onClick={google} loading={loading} />
          <OrDivider />
          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="stat-label block mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="w-full px-3 py-2.5 border border-ink-line bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                required
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="stat-label block">Password</label>
                <button type="button" onClick={() => { setView("forgot"); setErr(""); }} data-testid="forgot-password-link" className="text-[12px] text-brand hover:underline">
                  Forgot password?
                </button>
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="w-full px-3 py-2.5 border border-ink-line bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                required
              />
            </div>

            {unverified && <VerifyEmailNotice email={email} onResend={doResend} resending={resend.sending} resent={resend.sent} />}
            {err && (
              <div className="text-sm text-warn bg-warn-soft border border-warn/30 px-3 py-2 rounded-sm" data-testid="login-error">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full bg-brand hover:bg-brand-hover disabled:opacity-60 text-white font-medium py-2.5 rounded-sm flex items-center justify-center gap-2 text-sm transition-colors duration-150"
            >
              {loading ? "Signing in…" : (<>Sign in <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-ink-line text-sm text-ink-muted">
            Need an account?{" "}
            <Link to="/register" className="text-brand hover:underline font-medium" data-testid="register-link">
              Register
            </Link>
          </div>
          </>)}

          <div className="mt-6 flex items-center gap-2 text-[11px] text-ink-muted" data-testid="secured-by-firebase">
            <ShieldCheck className="w-3.5 h-3.5 text-good" /> Sign-in secured by Google Firebase Authentication
          </div>

          {/* Ownership marker — subtle, below the login card */}
          <div className="mt-10 pt-6 border-t border-ink-line flex flex-col items-center gap-1.5 opacity-90">
            <PoweredByTendStrat variant="light" size="sm" />
            <p className="text-[11px] text-ink-muted text-center">
              TWIN&trade; is a product of TendStrat Solutions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
